<template>
	<Card class="max-w-4xl mx-auto">
		<template #content>
			<div class="flex flex-col gap-8">
				<table class='text-sm text-left min-w-full divide-y divide-gray-300'>
					<thead>
					<tr>
						<th class="pb-3 text-sm font-semibold text-slate-800">{{ $t('forms.role.role_permissions_form.crud_permissions') }}</th>
						<th class="pb-3 text-sm font-semibold text-slate-800 text-center">{{ $t('forms.role.role_permissions_form.create') }}</th>
						<th class="pb-3 text-sm font-semibold text-slate-800 text-center">{{ $t('forms.role.role_permissions_form.read') }}</th>
						<th class="pb-3 text-sm font-semibold text-slate-800 text-center">{{ $t('forms.role.role_permissions_form.update') }}</th>
						<th class="pb-3 text-sm font-semibold text-slate-800 text-center">{{ $t('forms.role.role_permissions_form.delete') }}</th>
						<th class="pb-3 text-sm font-semibold text-slate-800 text-center">{{ $t('forms.role.role_permissions_form.select_all') }}</th>
					</tr>
					</thead>
					<tbody class='text-sm text-left min-w-full divide-y divide-gray-100'>
					<tr v-for="(permissions, key) in crudPermissionsGroupedByResource">
						<td><span class="text-sm font-medium text-slate-500 py-1.5 block">{{ key }}</span></td>
						<td class="w-20 text-center" v-for="action in ['create', 'read', 'update', 'delete']">
							<Checkbox v-if="permissions[action]" :disabled="true" v-model="form.permissions" :value="permissions[action].id"/>
						</td>
						<td class="w-20 text-center">
							<input type="checkbox" :disabled="true" :checked="isAllChecked(permissions, key)" @click="toggleAllChecked(permissions, key)"
							       class="h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-blue-600"/>
						</td>
					</tr>
					</tbody>
				</table>
				<ul>
					<li v-for="(permissions, key) in nonCrudPermissionsGroupedByResource" class="mt-6">
						<h6 class="text-sm font-semibold text-slate-700 py-1">{{ key }}</h6>
						<div class="w-full grid grid-cols-3">
							<div class="flex items-center justify-start text-xs font-medium text-slate-500 py-1.5" v-for="permission in permissions">
								<span class="text-sm font-medium text-slate-500 inline-block mr-3">{{ permission.action }}</span>
								<Checkbox :disabled="true" :value="permission.id" v-model="form.permissions" />
							</div>
						</div>
					</li>
				</ul>
			</div>
		</template>

		<template v-if="hasOnSaveListener || hasOnDeleteListener" #footer>
			<div class="flex justify-end space-x-3">
				<Button class="px-8" severity="danger" text v-if="hasOnDeleteListener" @click="$emit('on-delete', form)"
				        :label="$t('forms.common.delete')"
				        :disabled="true"/>
				<Button class="px-8" v-if="hasOnSaveListener" @click="$emit('on-save', form)" :label="$t('forms.common.save')"
				        :disabled="true"/>
			</div>
              <p class="text-sm text-gray-500 mt-2 text-right">
                Note: Editing permissions has been disabled for data integrity and testing reasons.<br />If you need to change a permission, please contact the system administrator.
              </p>
		</template>
	</Card>
</template>

<script>
import { mapStores } from "pinia";
import { usePermissionsStore } from "@/stores/permissions.js";

export default {
	name: "RolePermissionsForm",
	props: {
		form: {
			type: Object,
			required: true,
		},
	},
	computed: {
		...mapStores(usePermissionsStore),
		hasOnSaveListener() {
			return !!(this.$attrs?.onOnSave);
		},
		hasOnDeleteListener() {
			return !!(this.$attrs?.onOnDelete);
		},
		crudPermissions() {
			return this.permissionsStore.getPermissionOptions?.filter(p => {
				const [action] = p.name.split(':', 1);
				return ['create', 'read', 'update', 'delete'].includes(action);
			}) || [];
		},
		nonCrudPermissions() {
			return this.permissionsStore.getPermissionOptions?.filter(p => {
				const [action] = p.name.split(':', 1);
				return !['create', 'read', 'update', 'delete'].includes(action);
			}) || [];
		},
		crudPermissionsGroupedByResource() {
			const groupedPermissions = this.crudPermissions.reduce((previous, permission) => {
				const [action, resource] = permission.name.split(':', 2);
				if (!previous.hasOwnProperty(resource)) {
					previous[resource] = {
						create: null,
						read: null,
						update: null,
						delete: null,
					};
				}
				previous[resource][action] = {
					...permission,
					action,
				};
				return previous;
			}, {}) || {};

			// sort the keys on the object
			return Object.fromEntries(Object.entries(groupedPermissions).sort());
		},
		nonCrudPermissionsGroupedByResource() {
			const groupedPermissions = this.nonCrudPermissions.reduce((previous, permission) => {
				const [action, resource] = permission.name.split(':', 2);
				if (!previous.hasOwnProperty(resource)) {
					previous[resource] = {};
				}
				previous[resource][action] = {
					...permission,
					action,
				};
				return previous;
			}, {}) || {};

			// sort the keys on the object
			return Object.fromEntries(Object.entries(groupedPermissions).sort());
		},
	},
	async mounted() {
		await this.permissionsStore.indexPermissionOptions();
	},
	methods: {
		pluckPermissionIds(permissions) {
			let ids = [];
			for (const [key, value] of Object.entries(permissions)) {
				if (value?.id)
					ids.push(value.id);
			}
			return ids;
		},
		isAllChecked(permissions, action) {
			return this.pluckPermissionIds(permissions).every(p => this.form.permissions.includes(p));
		},
		toggleAllChecked(permissions, action) {
			const ids = this.pluckPermissionIds(permissions);
			if (this.isAllChecked(permissions, action)) {
				this.form.permissions = this.form.permissions.filter(id => !ids.includes(id));
			} else {
				this.form.permissions = [...new Set([...this.form.permissions, ...ids])];
			}
		},
	},
}
</script>

<style scoped>

</style>